import * as React from "react";
import * as moment from "moment";
import DatePicker from "react-datepicker";
import {Option, option} from "ts-option";
import MaskedDateInput from "../../hiberUiKit/src/atoms/Input/MaskedDateInput";
import IconWrapper from "../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import {evColors} from "./evChargingStyles";
import "./evChargingDateField.scss";

interface Props {
	value: Option<moment.Moment>;
	onChange: (value: Option<moment.Moment>) => void;
	/**
	 * The month the calendar opens on while no date is chosen.
	 */
	openToDate?: moment.Moment;
	minDate?: moment.Moment;
	maxDate?: moment.Moment;
	/**
	 * The date is what stops the period, so the field is outlined in red.
	 */
	invalid?: boolean;
}

interface State {
	calendarOpen: boolean;
}

/**
 * A bill date, typed as it reads on the bill or picked from a calendar. Typing stays, because dates are copied from
 * bills in series and the keyboard is quicker there.
 */
export default class EvChargingDateField extends React.Component<Props, State> {
	state: State = {calendarOpen: false};

	private root: HTMLDivElement | null = null;

	componentDidMount() {
		document.addEventListener("mousedown", this.closeOnOutsideClick);
		document.addEventListener("touchstart", this.closeOnOutsideClick);
	}

	componentWillUnmount() {
		document.removeEventListener("mousedown", this.closeOnOutsideClick);
		document.removeEventListener("touchstart", this.closeOnOutsideClick);
	}

	render() {
		const {value, onChange, openToDate, minDate, maxDate} = this.props;
		const selected = value.filter(m => m.isValid()).orNull || undefined;
		return (
			<div ref={el => this.root = el} style={styles.root}
				 className={`c-ev-date-field${this.props.invalid ? " is-invalid" : ""}`}>
				<MaskedDateInput value={value} onChange={onChange}/>
				<button type={"button"} style={styles.toggle} aria-label={"Pick a date"} aria-expanded={this.state.calendarOpen}
						onClick={() => this.setState({calendarOpen: !this.state.calendarOpen})}>
					<IconWrapper icon={Icon.Calendar} size={18} variant={"blue"}/>
				</button>
				{this.state.calendarOpen &&
				<div style={styles.calendar}>
					<DatePicker inline
								calendarClassName={"c-ev-date-calendar"}
								selected={selected}
								openToDate={selected || openToDate}
								minDate={minDate}
								maxDate={maxDate}
								onChange={this.pick}/>
				</div>}
			</div>
		);
	}

	private pick = (date: moment.Moment) => {
		this.setState({calendarOpen: false});
		this.props.onChange(option(moment(date).startOf("day")));
	};

	private closeOnOutsideClick = (evt: Event) => {
		if (this.state.calendarOpen && this.root && !this.root.contains(evt.target as Node)) {
			this.setState({calendarOpen: false});
		}
	};
}

const styles: { [key: string]: React.CSSProperties } = {
	root: {position: "relative"},
	toggle: {
		position: "absolute", top: 0, right: 0, height: 52, width: 52, display: "flex", alignItems: "center",
		justifyContent: "center", background: "none", border: 0, cursor: "pointer"
	},
	calendar: {
		position: "absolute", top: 56, left: 0, zIndex: 20, background: "#fff", borderRadius: 4,
		border: `1px solid ${evColors.border}`, boxShadow: "0 1px 8px 0 rgba(0, 0, 0, 0.1)"
	}
};
